<template>
  <v-dialog v-model="dialog" persistent max-width="1200px">
    <v-card grid-list-md>
      <ModelTitle title="New Customer" @close="close()" />
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <InnerTitle title="Name & Number" />
            <v-row>
              <v-col cols="12">
                <v-text-field
                  autofocus
                  :error-messages="nameErrors"
                  v-model="customer.name"
                  label="Name"
                  @change="customer.name = properCase(customer.name)"
                  :hide-details="nameErrors.length === 0"
                  outlined
                  @focus="$event.target.select()"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="customer.contactName"
                  outlined
                  hide-details
                  @change="
                    customer.contactName = properCase(customer.contactName)
                  "
                  dense
                  @focus="$event.target.select()"
                  label="Contact Name"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  outlined
                  dense
                  hide-details
                  class="right-input"
                  @focus="$event.target.select()"
                  v-model="customer.balanceLimit"
                  label="Balance Limit"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  @focus="$event.target.select()"
                  v-model="customer.cellNumber"
                  label="Cell Number"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  @focus="$event.target.select()"
                  v-model="customer.phoneNumber"
                  label="Phone Number"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  v-model="customer.email"
                  label="Email Address"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  v-model="customer.taxId"
                  label="Tax ID"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <DayPicker
                  v-model="customer.metadata.invoiceDue"
                  :initValue="customer.metadata.invoiceDue"
                  :title="'Customer Terms'"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <InnerTitle title="Addresses" />
            <div class="mb-3 subtitle-2 font-weight-bold black--text">
              Billing
            </div>
            <GoogleAddressInput
              label="Search Billing Address"
              @address-selected="setBillingAddress"
              class="mb-3"
            />
            <v-row>
              <v-col cols="12">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  v-model="customer.address"
                  label="Address"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  @change="customer.city = properCase(customer.city)"
                  v-model="customer.city"
                  label="City"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="customer.state"
                  label="State"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  v-model="customer.zip"
                  label="Zip Code"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <div class="subtitle-2 font-weight-bold black--text">
                  Shipping
                </div>
              </v-col>

              <v-col cols="6" v-if="customer.address">
                <v-btn outlined small @click="copyBillingAddressToShipping">
                  Same as billing
                </v-btn>
              </v-col>
              <v-col cols="12">
                <GoogleAddressInput
                  label="Search Shipping Address"
                  @address-selected="setShippingAddress"
                  class="mb-3"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  v-model="customer.shippingAddress"
                  label="Address"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  @change="
                    customer.shippingCity = properCase(customer.shippingCity)
                  "
                  v-model="customer.shippingCity"
                  label="City"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  outlined
                  dense
                  hide-details
                  v-model="customer.shippingState"
                  label="State"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  outlined
                  hide-details
                  dense
                  v-model="customer.shippingZip"
                  label="Zip Code"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <InnerTitle title="Other" />
            <v-row>
              <v-col cols="12">
                <SalesMan
                  v-model="selectedUser"
                  :rounded="false"
                  :initValue="this.salesRep"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  outlined
                  hide-details
                  dense
                  auto-grow
                  v-model="customer.note"
                  :label="$t('labels.note')"
                ></v-textarea>
              </v-col>
              <v-col
                cols="12"
                v-if="!currentBusiness.metadata.isSingleCustomerPricing"
              >
                <v-select
                  :items="tiers"
                  item-text="name"
                  item-value="id"
                  clearable
                  v-model="customer.PriceTierId"
                  label="Price Tier"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-row align="center">
                  <v-col class="py-0">
                    <div class="subtitle-2 font-weight-bold black--text">
                      Show/Hide Balance on Invoice
                    </div>
                    <div>
                      Toggle this option to display or hide the remaining
                      balance on printed or viewed customer invoices.
                    </div>
                  </v-col>
                  <v-col class="text-right shrink py-0">
                    <toggle-button
                      :width="60"
                      :labels="{ checked: 'Show', unchecked: 'Hide' }"
                      v-model="customer.showBalance"
                      :value="customer.showBalance"
                      :sync="true"
                    >
                    </toggle-button>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="close()">Close</v-btn>
        <v-btn color="primary" :loading="loading" dark @click="submit()"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import customerService from "../service.js";
import GlobalFunction from "@/mixins/GlobalFunction";
import SalesMan from "@/modules/User/components/AutoComplete.vue";
import DayPicker from "@/components/DayPicker";
import priceTierService from "@/modules/Product/PriceTier/service.js";
import GoogleAddressInput from "@/modules/Google/GoogleAddressInput.vue";
const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
  },
  name: "create-customer",
  data() {
    return {
      initUser: null,
      selectedTab: 0,
      tiers: [],
      foundError: false,
      loading: false,
      dialog: false,
      selectedUser: null,
      customer: {
        showBalance: true,
        name: null,
        cellNumber: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        shippingAddress: null,
        shippingCity: null,
        shippingState: null,
        shippingZip: null,
        note: null,
        SalesRepUserId: null,
        metadata: {
          invoiceDue: null,
        },
      },
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    customer: {
      name: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    salesRep() {
      if (this.initUser) {
        console.log("inituser", this.currentUser);
        if (this.checkRightStatus(50) && !this.currentUser.isAdmin) {
          this.initUser = this.currentUser;
          return this.initUser;
        } else {
          return null;
        }
      } else {
        // console.log('customer ', this.customer.SalesRep)
        return this.customer.SalesRep;
      }
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.customer.name.$dirty) return errors;
      if (!this.$v.customer.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
  },
  watch: {
    createDialog(val) {
      this.dialog = val;
      setTimeout(() => {
        if (this.checkRightStatus(50)) {
          this.initUser = this.currentUser;
        }
      }, 300);
      if (val && !this.currentBusiness.metadata.isSingleCustomerPricing) {
        this.getTiers();
      }
      console.log("this.currentUser;", this.currentUser);
    },
    selectedUser(val) {
      if (val && this.dialog) {
        this.customer.SalesRepUserId = val.id;
      }
    },
  },
  components: {
    SalesMan,
    DayPicker,
    GoogleAddressInput,
  },
  methods: {
    setBillingAddress(addressData) {
      this.customer.address = addressData.address;
      this.customer.city = addressData.city;
      this.customer.state = addressData.state;
      this.customer.zip = addressData.zip;
    },
    setShippingAddress(addressData) {
      this.customer.shippingAddress = addressData.address;
      this.customer.shippingCity = addressData.city;
      this.customer.shippingState = addressData.state;
      this.customer.shippingZip = addressData.zip;
    },
    async getTiers() {
      try {
        const response = await priceTierService.getAll();
        this.tiers = response.data;
      } catch (e) {
        console.error("Failed to load tiers", e);
      }
    },
    copyBillingAddressToShipping() {
      this.customer.shippingAddress = this.customer.address;
      this.customer.shippingCity = this.customer.city;
      this.customer.shippingState = this.customer.state;
      this.customer.shippingZip = this.customer.zip;
    },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.customer = {
        name: null,
        email: null,
        phoneNumber: null,
        contactName: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
        SalesRepUserId: null,
        metadata: {
          invoiceDue: null,
        },
      };
      this.initUser = null;
      this.selectedUser = null;
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        // if (!this.customer.SalesRepUserId) {
        //   this.$swal
        //     .mixin({
        //       toast: true,
        //       position: "top-end",
        //       showConfirmButton: false,
        //       timer: 3000,
        //     })
        //     .fire({
        //       icon: "error",
        //       title: "Sales Rep not selected!",
        //     });
        //   return false;
        // }
        this.loading = true;
        return customerService
          .create(this.customer)
          .then((result) => {
            if (result.status === "success") {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "customer is created",
                });
              this.loading = false;
              this.$emit("submit");
              this.$events.emit("customerListEvent");
              this.close();
            } else {
              this.loading = false;
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "error",
                  title: result.data.message
                    ? result.data.message
                    : "Internal Server Error",
                });
              this.close();
            }
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>

<style scoped></style>
